import { z } from 'zod';
import { permissionKeys } from './permissions.ts';

export const passwordSchema = z.string().min(12, 'Use at least 12 characters.').max(256);

// ---------- Multi-factor (TOTP) ----------
export const totpCodeSchema = z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code.');
export const recoveryCodeSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9]{5}-[a-z0-9]{5}$/, 'Recovery codes look like abcde-12345.');

export const mfaChallengeResponseSchema = z.object({ mfaRequired: z.literal(true), challenge: z.string() });
export type MfaChallengeResponse = z.infer<typeof mfaChallengeResponseSchema>;
export const mfaVerifySchema = z.object({ challenge: z.string().min(10).max(2000), code: z.union([totpCodeSchema, recoveryCodeSchema]) });
export type MfaVerify = z.infer<typeof mfaVerifySchema>;
export const mfaSetupResponseSchema = z.object({ secret: z.string(), otpauthUrl: z.string() });
export type MfaSetupResponse = z.infer<typeof mfaSetupResponseSchema>;
export const mfaConfirmSchema = z.object({ code: totpCodeSchema });
export const mfaDisableSchema = z.object({ password: z.string().min(1).max(256), code: z.union([totpCodeSchema, recoveryCodeSchema]) });

// ---------- Sessions ----------
export const sessionInfoSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  lastSeenAt: z.string().nullable(),
  expiresAt: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof sessionInfoSchema>;

export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(256), newPassword: passwordSchema });

// ---------- Password reset and invites ----------
export const resetRequestSchema = z.object({ email: z.string().trim().toLowerCase().email().max(254) });
export const resetConfirmSchema = z.object({ token: z.string().min(20).max(200), password: passwordSchema });

export const inviteCreateSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  roleKey: z.string().min(1).max(60),
  /** Optional record scope, stored for later enforcement (step 1 §88). */
  scope: z.object({ markets: z.array(z.string()).max(20).optional(), onlyAssignedLeads: z.boolean().optional() }).optional(),
});
export type InviteCreate = z.infer<typeof inviteCreateSchema>;
export const inviteAcceptSchema = z.object({ token: z.string().min(20).max(200), name: z.string().trim().min(1).max(120), password: passwordSchema });
export const inviteSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  roleKey: z.string(),
  roleName: z.string(),
  expiresAt: z.string(),
  createdAt: z.string(),
  status: z.enum(['pending', 'accepted', 'expired', 'revoked']),
});
export type InviteSummary = z.infer<typeof inviteSummarySchema>;
export const invitePreviewSchema = z.object({ email: z.string(), roleName: z.string() });

// ---------- Roles ----------
export const roleSummarySchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  system: z.boolean(),
  permissions: z.array(z.enum(permissionKeys)),
  userCount: z.number().int(),
});
export type RoleSummary = z.infer<typeof roleSummarySchema>;

export const roleKeySchema = z.string().trim().toLowerCase().regex(/^[a-z][a-z0-9-]{2,39}$/, 'Use 3–40 lowercase letters, digits or hyphens.');
export const roleCreateSchema = z.object({
  key: roleKeySchema,
  name: z.string().trim().min(2).max(60),
  description: z.string().trim().max(240).optional(),
  permissions: z.array(z.enum(permissionKeys)).max(permissionKeys.length),
});
export type RoleCreate = z.infer<typeof roleCreateSchema>;
export const roleUpdateSchema = z.object({
  name: z.string().trim().min(2).max(60).optional(),
  description: z.string().trim().max(240).nullable().optional(),
  permissions: z.array(z.enum(permissionKeys)).max(permissionKeys.length).optional(),
});
export type RoleUpdate = z.infer<typeof roleUpdateSchema>;
